import React, { useState, useEffect } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { EmailOutlinedIcon, UploadFileOutlinedIcon, GroupsOutlinedIcon, PersonAddAlt1OutlinedIcon } from 'icons/materialIcons';
import TeamV2MicrosoftIconStyled from '../common/TeamV2MicrosoftIconStyled';

const TeamV2MemberAddModal = ({
    isOpen,
    onClose,
    onSelect,
    anchorEl,
    offset = 20,
    horizontalOffset = 0
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (isOpen && anchorEl) {
            const calculatePosition = () => {
                const anchorRect = anchorEl.getBoundingClientRect();
                const viewportHeight = window.innerHeight;
                const modalHeight = 200; // Approximate modal height

                let top = anchorRect.bottom + offset;
                let left = anchorRect.right; // Start from the right edge

                // Ensure modal doesn't go off-screen on mobile
                if (isMobile) {
                    left = Math.min(Math.max(160, left), window.innerWidth - 160);
                }

                // Adjust if modal would go off bottom of screen
                if (top + modalHeight > viewportHeight) {
                    top = anchorRect.top - modalHeight - offset;
                }

                // Adjust if modal would go off right side of screen
                const modalWidth = 320; // Approximate modal width
                if (left + modalWidth > window.innerWidth) {
                    left = anchorRect.right - modalWidth; // Align right edge with button right edge
                }

                setModalPosition({ top, left });
            };

            calculatePosition();
            window.addEventListener('resize', calculatePosition);
            return () => window.removeEventListener('resize', calculatePosition);
        }
    }, [isOpen, anchorEl, offset, horizontalOffset, isMobile]);

    if (!isOpen) return null;

    const handleOverlayClick = (e) => {
        e.stopPropagation();
        onClose();
    };

    const optionStyles = {
        display: 'flex',
        alignItems: 'center',
        padding: isMobile ? '0.75rem' : '1rem',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        borderRadius: '0.5rem',
        '&:hover': {
            backgroundColor: '#f5f5f5',
        },
    };

    const iconStyles = {
        color: '#828282',
        fontSize: isMobile ? '1.25rem' : '1.5rem',
        marginRight: isMobile ? '0.75rem' : '1rem',
    };

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1100,
                backgroundColor: 'transparent',
            }}
            onClick={handleOverlayClick}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: `${modalPosition.top / 16}rem`,
                    left: `${modalPosition.left / 16}rem`,
                    transform: 'translateX(0)',
                    backgroundColor: '#fff',
                    borderRadius: '0.5rem',
                    width: isMobile ? 'calc(100% - 2rem)' : '90%',
                    maxWidth: '20rem',
                    boxShadow: '0 0.25rem 1rem rgba(0, 0, 0, 0.08), 0 0.125rem 0.5rem rgba(0, 0, 0, 0.04)',
                    margin: isMobile ? '0 1rem' : 0,
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {/* Add by Email Option */}
                    <Box
                        sx={optionStyles}
                        onClick={() => onSelect('add-email')}
                    >
                        <EmailOutlinedIcon sx={iconStyles} />
                        <Box>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: isMobile ? '0.875rem' : '0.9rem',
                                    color: '#333',
                                }}
                            >
                                Add by Email
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: isMobile ? '0.75rem' : '0.8rem',
                                    color: '#828282',
                                }}
                            >
                                Add teammates via email address
                            </Typography>
                        </Box>
                    </Box>

                    {/* Import via CSV Option */}
                    <Box
                        sx={optionStyles}
                        onClick={() => onSelect('import-csv')}
                    >
                        <UploadFileOutlinedIcon sx={iconStyles} />
                        <Box>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: isMobile ? '0.875rem' : '0.9rem',
                                    color: '#333',
                                }}
                            >
                                Import via CSV
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: isMobile ? '0.75rem' : '0.8rem',
                                    color: '#828282',
                                }}
                            >
                                Add teammates via CSV file
                            </Typography>
                        </Box>
                    </Box>

                    {/* Create Un-assigned Members Option */}
                    <Box
                        sx={optionStyles}
                        onClick={() => onSelect('create-members')}
                    >
                        <PersonAddAlt1OutlinedIcon sx={iconStyles} />
                        <Box>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: isMobile ? '0.875rem' : '0.9rem',
                                    color: '#333',
                                }}
                            >
                                Unassigned Members
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: isMobile ? '0.75rem' : '0.8rem',
                                    color: '#828282',
                                }}
                            >
                                Create members then assign to teammates later
                            </Typography>
                        </Box>
                    </Box>

                    {/* Sync with Microsoft AD Option */}
                    <Box
                        sx={optionStyles}
                        onClick={() => onSelect('sync-ad')}
                    >
                        <TeamV2MicrosoftIconStyled
                            size="1.25rem"
                            marginRight={isMobile ? '0.75rem' : '1rem'}
                        />
                        <Box>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: isMobile ? '0.875rem' : '0.9rem',
                                    color: '#333',
                                }}
                            >
                                Sync Active Directory
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: isMobile ? '0.75rem' : '0.8rem',
                                    color: '#828282',
                                }}
                            >
                                Sync teammates from Microsoft Active Directory
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default TeamV2MemberAddModal;